// apps/web/src/features/russia-requests/components/editor/RussiaEditorFooter.tsx
//
// Sticky save/cancel footer with the autosave indicator. Mirrors the
// china `DraftEditorFooter` status-line pattern but trimmed to what the
// russia manual editor surfaces (no send/close/delete here).

import { useTranslation } from 'react-i18next';
import { Button } from '../../../../ui/index.js';
import type { AutosaveState } from './useRussiaEditorAutosave.js';

export interface RussiaEditorFooterProps {
  autosave: AutosaveState;
  canSubmit: boolean;
  submitting: boolean;
  /** Edit mode swaps the create-verb save labels for the save-changes ones. */
  editMode: boolean;
  onCancel: () => void;
}

export function RussiaEditorFooter(props: RussiaEditorFooterProps) {
  const { autosave, canSubmit, submitting, editMode, onCancel } = props;
  const { t } = useTranslation('russiaRequests');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 20px',
        borderTop: '1px solid var(--border-1)',
      }}
    >
      <AutosaveStatus state={autosave} />
      <div style={{ flex: 1 }} />
      <Button type="button" kind="secondary" onClick={onCancel}>
        {t('editor.cancel')}
      </Button>
      <Button
        type="submit"
        kind="primary"
        disabled={!canSubmit}
        data-testid="russia-requests-editor-submit"
      >
        {submitting
          ? t(editMode ? 'editor.editSaving' : 'editor.saving')
          : t(editMode ? 'editor.editSave' : 'editor.save')}
      </Button>
    </div>
  );
}

function AutosaveStatus({ state }: { state: AutosaveState }) {
  const { t } = useTranslation('russiaRequests');

  if (state.status === 'saving') {
    return (
      <span
        data-testid="russia-requests-editor-autosave-status"
        style={{ fontSize: 12, color: 'var(--fg-3)' }}
      >
        {t('editor.autosave.saving')}
      </span>
    );
  }
  if (state.status === 'saved' && state.savedAt) {
    return (
      <span
        data-testid="russia-requests-editor-autosave-status"
        style={{ fontSize: 12, color: 'var(--fg-3)' }}
      >
        {t('editor.autosave.savedAt', { time: formatTime(state.savedAt) })}
      </span>
    );
  }
  if (state.status === 'error') {
    return (
      <span
        data-testid="russia-requests-editor-autosave-status"
        role="status"
        style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
      >
        {t('editor.autosave.error')}
      </span>
    );
  }
  return <span data-testid="russia-requests-editor-autosave-status" style={{ fontSize: 12 }} />;
}

function formatTime(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
